import { yupResolver } from '@hookform/resolvers/yup'
import 'dayjs/locale/en'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import InputMask from 'react-input-mask'
import * as yup from 'yup'

import {
  Box,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextareaAutosize,
  styled
} from '@mui/material'

import { ErrorText, MButton, ProgressLoaders, Text } from 'components'
import { MESSAGES, ROUTES_PATH, TOPIC_ITEMS, USER_TOKEN } from 'config'
import { useAuth } from 'context'
import { APIs } from 'services'
import { useLocalStorage } from 'utils'
import { isEmpty } from 'lodash'

const schema = yup.object({
  name: yup.string().required('Name is required'),
  accountNumber: yup.string(),
  serviceAddress: yup.string(),
  email: yup.string().email('Please enter a valid email address').required('Email is required'),
  number: yup.string()
    .required('Contact number is required')
    .test('valid-phone', 'Please enter a valid phone number', (value) => {
      if (!value) return false
      return value.replace(/([()_\- ])/g, '').length === 10
    }),
  topic: yup.string().required('Please select a topic').notOneOf([''], 'Please select a topic'),
  subject: yup.string().required('Subject is required'),
  comments: yup.string()
}).required()

export const Form: React.FC = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { account, getAccount, currentAccount, isAdmin, userEmail } = useAuth()
  const [authToken] = useLocalStorage(USER_TOKEN, '')
  const [prevAccountId, setPrevAccountId] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, isValid }
  } = useForm({
    mode: 'all',
    defaultValues: {
      name: '',
      accountNumber: '',
      serviceAddress: '',
      email: '',
      number: '',
      topic: '',
      subject: '',
      comments: ''
    },
    resolver: yupResolver(schema)
  })

  useEffect(() => {
    if (prevAccountId === currentAccount?.accountId || currentAccount?.accountId === account?.myAccount?.accountId) {
      return
    }
    if (currentAccount?.accountId !== null) {
      void getAccount({
        AccessToken: authToken,
        accountId: currentAccount?.accountId,
        admin: isAdmin,
        email: userEmail
      })
    }
    setPrevAccountId((currentAccount?.accountId ?? account?.myAccount?.accountId) ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, currentAccount])

  useEffect(() => {
    if (account != null) {
      setValue('name', account.myAccount.personName ?? '')
      setValue('accountNumber', account.myAccount.accountId ?? '')
      setValue('serviceAddress', account.myAccount.serviceAddresses?.[0]?.serviceAddress ?? '')
      setValue('email', account.myAccount.email ?? '')
      setValue('number', account?.myAccount?.mobileNumber ?? account?.myAccount?.alternateNumber ?? '')
    }
  }, [account, setValue])

  const isFieldDisabled = (fieldName: string): boolean => {
    if (account == null) return false
    switch (fieldName) {
      case 'name': return !isEmpty(account?.myAccount?.personName)
      case 'accountNumber': return !isEmpty(account?.myAccount?.accountId)
      case 'serviceAddress': return !isEmpty(account?.myAccount?.serviceAddresses?.[0]?.serviceAddress)
      case 'email': return !isEmpty(account?.myAccount?.email)
      case 'number': return !isEmpty(account?.myAccount?.alternateNumber)
      default: return false
    }
  }

  const onSubmit = async (data: yup.InferType<typeof schema>): Promise<void> => {
    setIsSubmitting(true)
    try {
      const body = {
        ...data,
        AccessToken: authToken
      }

      await APIs.contactUs(body as Parameters<typeof APIs.contactUs>[0])
      enqueueSnackbar(MESSAGES.FORM_SUBMIT_SUCCESS, {
        variant: 'success'
      })
      setTimeout(() => {
        navigate(ROUTES_PATH.DASHBOARD)
      }, 7000)
    } catch (e) {
      enqueueSnackbar(MESSAGES.FORM_SUBMIT_ERROR, {
        variant: 'error'
      })
    }
    setIsSubmitting(false)
  }

  return (
    <>
      {account !== null
        ? <form onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" flexDirection="column" flex={1} gap={3} mr={2}>
            <Box>
              <Text fontSize="22px" fontWeight="700" color="#010B0E">
                Contact Us Form
              </Text>
              <span style={{ color: '#010B0E', fontSize: '16px', fontWeight: '900' }}>
                {' '}
                Please allow 2-3 business days for response.
              </span>
            </Box>
            <Box>
              <FormLabel>Name (Last, First)*</FormLabel>
              <Controller
                name="name"
                control={control}
                render={({ field: { onChange, value, name } }): JSX.Element => (
                  <OutlinedInput
                    fullWidth
                    type="text"
                    name={name}
                    placeholder="Customer Name"
                    onChange={onChange}
                    value={value}
                    disabled={isFieldDisabled('name')}
                  />
                )}
              />
              {errors.name && <ErrorText>{errors.name.message}</ErrorText>}
            </Box>
            <Box>
              <FormLabel>Account Number </FormLabel>
              <Controller
                name="accountNumber"
                control={control}
                render={({ field: { onChange, value, name } }): JSX.Element => (
                  <OutlinedInput
                    fullWidth
                    type="text"
                    name={name}
                    placeholder="Account Number"
                    onChange={onChange}
                    value={value}
                    disabled={isFieldDisabled('accountNumber')}
                  />
                )}
              />
            </Box>
            <Box>
              <FormLabel>Service Address</FormLabel>
              <Controller
                name="serviceAddress"
                control={control}
                render={({ field: { onChange, value, name } }): JSX.Element => (
                  <OutlinedInput
                    fullWidth
                    type="text"
                    name={name}
                    placeholder="Service Address"
                    onChange={onChange}
                    value={value}
                    disabled={isFieldDisabled('serviceAddress')}
                  />
                )}
              />
            </Box>
            <Box>
              <FormLabel>Email Address *</FormLabel>
              <Controller
                name="email"
                control={control}
                render={({ field: { onChange, value, name } }): JSX.Element => (
                  <OutlinedInput
                    fullWidth
                    type="text"
                    name={name}
                    placeholder="Email Address"
                    onChange={onChange}
                    value={value}
                    disabled={isFieldDisabled('email')}
                  />
                )}
              />
              {errors.email && <ErrorText>{errors.email.message}</ErrorText>}
            </Box>
            <Box>
              <FormLabel>Contact Number *</FormLabel>
              <Controller
                name="number"
                control={control}
                render={({ field: { onChange, value } }): JSX.Element => (
                  <InputMask
                    mask="(999) 999-9999"
                    value={value}
                    maskChar="_"
                    type="text"
                    onChange={onChange}
                    disabled={isFieldDisabled('number')}
                  >
                    <OutlinedInput fullWidth placeholder="Contact Number" />
                  </InputMask>
                )}
              />
              {errors.number && <ErrorText>{errors.number.message}</ErrorText>}
            </Box>
            <Box>
              <FormLabel>Topic *</FormLabel>
              <Controller
                name="topic"
                control={control}
                render={({ field: { onChange, value } }): JSX.Element => (
                  <Select
                    fullWidth
                    displayEmpty
                    value={value}
                    onChange={onChange}
                  >
                    <MenuItem value="" disabled>
                      Select a topic
                    </MenuItem>
                    {TOPIC_ITEMS.map((item, index) => (
                      <MenuItem key={index} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.topic && <ErrorText>{errors.topic.message}</ErrorText>}
            </Box>
            <Box>
              <FormLabel>Subject *</FormLabel>
              <Controller
                name="subject"
                control={control}
                render={({ field: { onChange, value, name } }): JSX.Element => (
                  <OutlinedInput
                    fullWidth
                    type="text"
                    name={name}
                    placeholder="Subject"
                    onChange={onChange}
                    value={value}
                  />
                )}
              />
              {errors.subject && <ErrorText>{errors.subject.message}</ErrorText>}
            </Box>
            <Box>
              <FormLabel>ADDITIONAL COMMENTS (OPTIONAL)</FormLabel>
              <Controller
                name="comments"
                control={control}
                render={({ field: { onChange, value } }): JSX.Element => (
                  <Textarea
                    placeholder="Additional Comments"
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
              <span style={{ color: '#5D6365', fontSize: '12px', fontWeight: '400' }}>
                (*) Indicates a required field
              </span>
            </Box>
            <Box>
              <MButton
                variant="contained"
                rounded="true"
                texttransform="none"
                type="submit"
                size="large"
                loading={isSubmitting}
                disabled={!isValid || isSubmitting}
              >
                Submit Form
              </MButton>
            </Box>
          </Box>
        </form>
        : <Box my={2}><ProgressLoaders height="100%" /></Box>}
    </>
  )
}

export const Textarea = styled(TextareaAutosize)({
  width: '100%',
  height: '188px !important',
  resize: 'none',
  borderRadius: '2px',
  border: '2px solid #E5E5F6 !important',
  padding: '10px',
  font: 'inherit'
})

export const FormLabel = styled(InputLabel)`
  font-weight: 400;
  font-size: 12px;
  color: #4d515f;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 10px;
`
